import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/client'
import { GET_USER_WATCHLISTS } from '../../graphql/queries'
import {
  CREATE_WATCHLIST,
  ADD_PROJECT_TO_WATCHLIST,
} from '../../graphql/mutations'
import { useAuth } from '../../hooks/useAuth'
import type { Watchlist } from '../../types'

interface AddToWatchlistModalProps {
  open: boolean
  onClose: () => void
  projectId: string
  projectName: string
}

const MAX_WATCHLISTS = 3

export function AddToWatchlistModal({
  open,
  onClose,
  projectId,
  projectName,
}: AddToWatchlistModalProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [showCreateNew, setShowCreateNew] = useState(false)
  const [newListName, setNewListName] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Get user's watchlists
  const { data, loading, refetch } = useQuery(GET_USER_WATCHLISTS, {
    variables: { userId: user?.id },
    skip: !user?.id,
  })

  const watchlists: Watchlist[] = data?.getUserWatchlists || []

  // Create watchlist mutation
  const [createWatchlist, { loading: creating }] = useMutation(CREATE_WATCHLIST, {
    onCompleted: async (data) => {
      // Add project to the newly created watchlist
      await addToWatchlist({
        variables: {
          input: {
            watchlistId: data.createWatchlist.id,
            projectId,
          },
        },
      })
      setNewListName('')
      setShowCreateNew(false)
      await refetch()
    },
    onError: (err) => {
      setError(err.message)
    },
  })

  // Add to watchlist mutation
  const [addToWatchlist, { loading: adding }] = useMutation(ADD_PROJECT_TO_WATCHLIST, {
    onCompleted: () => {
      onClose()
    },
    onError: (err) => {
      setError(err.message)
    },
  })

  const handleCreateNew = () => {
    if (watchlists.length >= MAX_WATCHLISTS) {
      setError(t('projects.watchlists.maxReached', { max: MAX_WATCHLISTS }))
      return
    }
    setShowCreateNew(true)
    setError(null)
  }

  const handleCreate = async () => {
    if (!newListName.trim()) return

    await createWatchlist({
      variables: {
        input: {
          userId: user?.id,
          name: newListName.trim(),
        },
      },
    })
  }

  const handleAddToList = async (watchlistId: string) => {
    await addToWatchlist({
      variables: {
        input: {
          watchlistId,
          projectId,
        },
      },
    })
  }

  const handleClose = () => {
    setShowCreateNew(false)
    setNewListName('')
    setError(null)
    onClose()
  }

  useEffect(() => {
    if (open) {
      setShowCreateNew(false)
      setNewListName('')
      setError(null)
    }
  }, [open])

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('projects.watchlists.addToList')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {projectName}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : showCreateNew ? (
          <Box>
            <TextField
              autoFocus
              fullWidth
              label={t('projects.watchlists.listName')}
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleCreate()
                }
              }}
              disabled={creating}
            />
          </Box>
        ) : (
          <Box>
            {watchlists.length === 0 ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
                {t('projects.watchlists.noLists')}
              </Typography>
            ) : (
              <List>
                {watchlists.map((watchlist) => (
                  <ListItem key={watchlist.id} disablePadding>
                    <ListItemButton
                      onClick={() => handleAddToList(watchlist.id)}
                      disabled={adding || watchlist.projectIds.includes(projectId)}
                    >
                      <ListItemText
                        primary={watchlist.name}
                        secondary={`${watchlist.projectIds.length} ${t('navigation.projects').toLowerCase()}`}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {showCreateNew ? (
          <>
            <Button onClick={() => setShowCreateNew(false)} disabled={creating}>
              {t('projects.watchlists.cancel')}
            </Button>
            <Button
              onClick={handleCreate}
              variant="contained"
              disabled={!newListName.trim() || creating}
            >
              {creating ? <CircularProgress size={24} /> : t('projects.watchlists.create')}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose}>{t('projects.watchlists.cancel')}</Button>
            <Button
              onClick={handleCreateNew}
              variant="contained"
              disabled={watchlists.length >= MAX_WATCHLISTS}
            >
              {t('projects.watchlists.createNew')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
